/* eslint-disable max-statements */
import { Button, Group, Pill, Stack, Text, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';

import { SearchArchivedBatchesResponse } from '@e-origin/contracts';
import { TemplateType } from '@e-origin/shared';

import { Select } from '@components';
import { DEBOUNCE_TIME } from '@constants';
import { useSearchArchivedBatches, useUnarchiveBatches } from '@hooks/batch';

interface UnarchiveBatchesProps {
  title: string;
}

export const UnarchiveBatches = ({ title }: UnarchiveBatchesProps) => {
  const [selectedBatches, setSelectedBatches] = useState<SearchArchivedBatchesResponse>([]);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(searchValue, DEBOUNCE_TIME);
  const unarchiveBatchesMutation = useUnarchiveBatches();
  const { data: searchResults = [], isFetching } = useSearchArchivedBatches({ search: debouncedSearchValue });

  const batchesMap = useMemo(() => new Map(searchResults.map((batch) => [batch.value, batch])), [searchResults]);
  const availableOptions = useMemo(
    () =>
      searchResults
        .filter((result) => !selectedBatches.some((batch) => batch.value === result.value))
        .map((result) => ({
          label: result.name,
          value: result.value,
        })),
    [searchResults, selectedBatches],
  );

  const highlight = useCallback((text: string, search: string) => {
    if (!search) return text;

    const regex = new RegExp(search, 'gi');
    const parts = text.split(regex);
    const matches = text.match(regex) || [];

    return (
      <span>
        {parts.map((part, index) => (
          <span key={index}>
            {part}
            {matches[index] && <b>{matches[index]}</b>}
          </span>
        ))}
      </span>
    );
  }, []);

  const renderSelectOption = useCallback(
    ({ option }) => {
      const batch = batchesMap.get(option.value);
      if (!batch) return null;

      return (
        <Stack gap="xs">
          <Text size="md" lh={0} mt="xs">
            {highlight(batch.name, batch.searchValue)}
          </Text>
          <Text c="dimmed" size="xs" lh={1}>
            {batch.templateType === TemplateType.HIGH_VALUE_H1 ? 'HV' : 'LV'} -
            {batch.mrn ? (
              <>
                MRN: <span>{highlight(batch.mrn, batch.searchValue)}</span>
              </>
            ) : (
              'MRN N/A'
            )}
          </Text>
        </Stack>
      );
    },
    [batchesMap, highlight],
  );

  const handleAddBatch = useCallback(
    (batchValue: string) => {
      const batch = batchesMap.get(batchValue);
      if (!batch) return;

      setSelectedBatches((prev) => [...prev, batch]);
      setSearchValue('');
    },
    [batchesMap],
  );

  const handleRemoveBatch = useCallback((batchValue: string) => {
    setSelectedBatches((prev) => prev.filter((batch) => batch.value !== batchValue));
  }, []);

  const handleUnarchive = useCallback(() => {
    const batchValues = selectedBatches.map((batch) => batch.value);
    unarchiveBatchesMutation.mutate({ batches: batchValues });
    setSelectedBatches([]);
  }, [selectedBatches, unarchiveBatchesMutation]);

  const getNotFoundMessage = () => {
    if (!debouncedSearchValue) return 'Start typing to search...';
    if (debouncedSearchValue && !isFetching && searchResults.length === 0) return 'No results found';
    return 'Loading...';
  };

  const buttonText = selectedBatches.length > 1 ? 'Unarchive batches' : 'Unarchive batch';

  return (
    <Stack gap="xl">
      <Title order={2}>{title}</Title>

      <Stack>
        <Select
          label="Type a batch name or MRN"
          data={availableOptions}
          value={null}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onOptionSubmit={handleAddBatch}
          searchable
          size="lg"
          renderOption={renderSelectOption}
          nothingFoundMessage={getNotFoundMessage()}
        />

        {selectedBatches.length > 0 && (
          <Pill.Group>
            {selectedBatches.map((batch, index) => (
              <Pill key={`${batch.value}-${index}`} size="sm" withRemoveButton onRemove={() => handleRemoveBatch(batch.value)} bg="blue" c="white">
                {batch.name} ({batch.templateType === TemplateType.HIGH_VALUE_H1 ? 'HV' : 'LV'})
              </Pill>
            ))}
          </Pill.Group>
        )}
      </Stack>

      <Group>
        <Button onClick={handleUnarchive} disabled={selectedBatches.length === 0} variant="filled" size="lg">
          {buttonText}
        </Button>
      </Group>
    </Stack>
  );
};
